/**
 * Network health check utility for detecting ISP-level blocking of backend services.
 * Some ISPs/routers block or throttle connections to specific domains (e.g., supabase.co)
 * via DNS filtering or SNI inspection. This utility detects such blocking.
 *
 * IMPORTANT: This utility is intentionally conservative to avoid false positives.
 * It only alerts users when the network is truly blocked (multiple consecutive timeouts),
 * not for occasional slow responses which are normal on mobile/shared networks.
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const HEALTH_CHECK_TIMEOUT = 15000; // 15 seconds — generous timeout to avoid false positives
const CHECK_INTERVAL = 60000; // 60 seconds between checks when unhealthy
const CONSECUTIVE_FAILURES_BEFORE_ALERT = 3; // Only alert after 3 consecutive failures

let consecutiveFailures = 0;

export type NetworkHealthStatus = "healthy" | "degraded" | "blocked" | "offline";

interface HealthCheckResult {
  status: NetworkHealthStatus;
  latencyMs: number | null;
  error?: string;
}

/**
 * Performs a lightweight connectivity check against the backend.
 * Uses a HEAD request to minimize data transfer.
 */
export async function checkBackendConnectivity(): Promise<HealthCheckResult> {
  if (!navigator.onLine) {
    return { status: "offline", latencyMs: null };
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), HEALTH_CHECK_TIMEOUT);
  const start = performance.now();

  try {
    const response = await fetch(`${SUPABASE_URL}/rest/v1/`, {
      method: "HEAD",
      signal: controller.signal,
      headers: {
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
    });

    clearTimeout(timeout);
    const latencyMs = Math.round(performance.now() - start);

    if (response.ok || response.status === 400) {
      // 400 is expected for a bare /rest/v1/ call — it means we reached the server
      consecutiveFailures = 0;
      return { status: "healthy", latencyMs };
    }

    // Non-OK but we reached the server — still reachable
    consecutiveFailures = 0;
    return { status: "healthy", latencyMs };
  } catch (error) {
    clearTimeout(timeout);
    const latencyMs = Math.round(performance.now() - start);
    consecutiveFailures++;

    if (error instanceof DOMException && error.name === "AbortError") {
      // Only classify as "blocked" after multiple consecutive timeouts
      if (consecutiveFailures >= CONSECUTIVE_FAILURES_BEFORE_ALERT) {
        return {
          status: "blocked",
          latencyMs,
          error: "Request timed out — your network may be blocking this service",
        };
      }
      // First few timeouts are treated as transient — don't alert user
      return { status: "healthy", latencyMs };
    }

    // Other network errors — only escalate after multiple consecutive failures
    if (consecutiveFailures >= CONSECUTIVE_FAILURES_BEFORE_ALERT) {
      return {
        status: "degraded",
        latencyMs,
        error: error instanceof Error ? error.message : "Network error",
      };
    }

    // Transient error — don't alert
    return { status: "healthy", latencyMs };
  }
}

type HealthListener = (result: HealthCheckResult) => void;

let listeners: HealthListener[] = [];
let monitorInterval: ReturnType<typeof setInterval> | null = null;
let lastResult: HealthCheckResult | null = null;

export function subscribeToHealthStatus(listener: HealthListener): () => void {
  listeners.push(listener);
  // Only replay the last result if it's healthy — replaying a stale negative
  // result (degraded/blocked) would show the warning banner to new subscribers
  // even if the connection has since recovered.
  if (lastResult && lastResult.status === "healthy") listener(lastResult);
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

function notifyListeners(result: HealthCheckResult) {
  lastResult = result;
  listeners.forEach((l) => l(result));
}

export function startHealthMonitor() {
  if (monitorInterval) return;

  // Initial check after 15 seconds — give the app plenty of time to settle.
  // The first check alone won't trigger alerts (needs 3 consecutive failures).
  setTimeout(async () => {
    const result = await checkBackendConnectivity();
    notifyListeners(result);

    // Only continue monitoring if unhealthy
    if (result.status !== "healthy") {
      monitorInterval = setInterval(async () => {
        const r = await checkBackendConnectivity();
        notifyListeners(r);
        if (r.status === "healthy" && monitorInterval) {
          clearInterval(monitorInterval);
          monitorInterval = null;
        }
      }, CHECK_INTERVAL);
    }
  }, 15000);

  // Re-check on online/offline transitions
  window.addEventListener("online", async () => {
    consecutiveFailures = 0;
    const result = await checkBackendConnectivity();
    notifyListeners(result);
  });
}

export function getLastHealthResult(): HealthCheckResult | null {
  return lastResult;
}
